import { create } from "zustand";
import { getSelectedShapeName } from "@/utils/sharePricePerformance/selectedPlaceHolderLocalStorage";
import { Model } from "@alphascriptsolutions/client-core";

type FeatureState = {
  percentageChangeChart: boolean;
  actualTradedValueChart: boolean;
  addCustomBuiltIndices: boolean;
  noCustomBuiltIndices: boolean;
  addConstantLineSelection: boolean;
  noConstantLineSelection: boolean;
  addsecondaryVerticalAxisColumnChart: boolean;
  nosecondaryVerticalAxisColumnChart: boolean;
  addsummaryTable: boolean;
  nosummaryTable: boolean;
  addSmallLineChart: boolean;
  nosmallLineChart: boolean;
  addCallOutBoxes: boolean;
  noCallOutBoxes: boolean;
  addBackgroundShading: boolean;
  noBackgroundShading: boolean;
  addVerticalLine: boolean;
  noVerticalLine: boolean;
};
interface StoreState {
  disabledButton: boolean;
  isChatCompleted: boolean;
  currentStep: number;
  answersByStep: number[];
  questionsPerStep: number[];
  customFeatureModel: Model;
  model: Model;
  completeFormatDataRef: any[];
  documentData: {
    llmGlobalState: any;
    documentContent: any;
  };
  initialInputsStep: Record<string, boolean>;
  selectedFeatureStep: Record<string, boolean>;
  disableBackButton: boolean;
  isEditInitial: boolean;
  selectedFeatureMessagesData: Record<string, object[]>;
  data: FeatureState;
  popup: string;
  featuresData: {
    featuresToDelete?: Array<{
      featureId: string;
      instanceId?: string;
    }>;
    featuresToAdd?: string[];
    featuresToUpdate?: Array<{
      featureId: string;
      instanceId?: string;
    }>;
  };

  // actions
  setDisabledButton: (disabled: boolean) => void;
  setIsChatCompleted: (disabled: boolean) => void;
  setCurrentStep: (step: number) => void;
  markAnswered: (step: number) => void;
  updateAnswerByStep: (step: number, value: number) => void; // ⬅️ new helper
  nextStep: () => void; // ⬅️ new helper
  setCustomFeatureModel: (model: Model) => void;
  setModel: (model: Model) => void;
  setCompleteFormatData: (data: any[]) => void;
  setDocumentData: (llmData: any, documentContent: any) => void;
  updateQuestionsForStep: (step: number, question: number) => void;
  setInitialInputStep: (featureKey: string, value: boolean) => void;
  getInitialInputStep: (featureKey: string) => boolean | undefined;
  setSelectedFeature: (featureKey: string, value: boolean) => void;
  getSelectedFeature: (featureKey: string) => boolean | undefined;
  setDisableBackButton: (disableBackButton: boolean) => void;
  setIsEditInitial: (isEditInitial: boolean) => void;
  setSelectedFeatureMessagesData: (featureName: string, message: any) => void;
  setData: (button1: { name: string; value: boolean }, button2: { name: string; value: boolean }) => void;
  setCompleteData: (data: any) => void;
  addFeature: (featureId: string) => void;
  deleteFeature: (featureId: string, instanceId?: string) => void;
  updateFeature: (featureId: string, instanceId?: string) => void;
  removeFeature: (featureId: string) => void;
  removeFeatureByInstanceId: (featureId: string, instanceId: string) => void;
  setPopup: (popup: string) => void;
  isInstanceIdInDelete: (featureId: string, instanceId: string) => boolean;
  isInstanceIdInUpdate: (featureId: string, instanceId: string) => boolean;
  addFeatureToAdd: (id: string) => void;
  removeFeatureFromAdd: (id: string) => void;
}

export const useAlphaAgentStore = create<StoreState>((set, get) => {
  const selectedPlaceholderName = getSelectedShapeName();
  return {
    /* ---------- state ---------- */
    disabledButton: true,
    isChatCompleted: false,
    currentStep: selectedPlaceholderName ? 3 : 0,
    questionsPerStep: [4, 9, 1, 3],
    answersByStep: selectedPlaceholderName ? [4, 9, 1, 3] : [0, 0, 0, 2],
    customFeatureModel: null,
    model: null,
    completeFormatDataRef: [],
    documentData: { llmGlobalState: {}, documentContent: {} },
    initialInputsStep: {
      primaryCompany: false,
      timePeriod: false,
      currency: false,
    },
    selectedFeatureStep: {},
    disableBackButton: false,
    isEditInitial: false,
    selectedFeatureMessagesData: {},
    data: {
      actualTradedValueChart: false,
      addBackgroundShading: false,
      addCallOutBoxes: false,
      addConstantLineSelection: false,
      addCustomBuiltIndices: false,
      addSmallLineChart: false,
      addVerticalLine: false,
      addsecondaryVerticalAxisColumnChart: false,
      addsummaryTable: false,
      noBackgroundShading: false,
      noCallOutBoxes: false,
      noConstantLineSelection: false,
      noCustomBuiltIndices: false,
      noVerticalLine: false,
      nosecondaryVerticalAxisColumnChart: false,
      nosmallLineChart: false,
      nosummaryTable: false,
      percentageChangeChart: false,
    },
    popup: "",
    featuresData: {
      featuresToDelete: [],
      featuresToAdd: [],
      featuresToUpdate: [],
    },

    /* ---------- setters ---------- */
    setDisabledButton: (disabled) => set({ disabledButton: disabled }),
    setIsChatCompleted: (disabled) => set({ isChatCompleted: disabled }),
    setCurrentStep: (step) => set({ currentStep: step }),
    setCustomFeatureModel: (model) => set({ customFeatureModel: model }),
    setModel: (data) => set({ model: data }),
    setCompleteFormatData: (data) => set({ completeFormatDataRef: data }),
    setDocumentData: (llmData, documentContent) =>
      set((state) => {
        const shouldReplaceLLM = !(llmData === undefined || llmData === '' || (typeof llmData === 'string' && llmData.trim() === '{}'));
        const shouldReplaceDoc = !(documentContent === undefined || documentContent === '' || (typeof documentContent === 'string' && documentContent.trim() === '{}'));

        return {
          documentData: {
            llmGlobalState: shouldReplaceLLM ? llmData : state.documentData.llmGlobalState,
            documentContent: shouldReplaceDoc ? documentContent : state.documentData.documentContent,
          },
        };
      }),
    setDisableBackButton: (disableBackButton: boolean) => set({ disableBackButton: disableBackButton }),
    setIsEditInitial: (isEditInitial: boolean) => set({ isEditInitial }),
    setCompleteData: (data: any) => set({ data }),
    setPopup: (popup: string) => set({ popup: popup }),

    /* ---------- business logic ---------- */
    markAnswered: (step) => {
      const { answersByStep, questionsPerStep } = get();
      const next = [...answersByStep];

      next[step] = Math.min(next[step] + 1, questionsPerStep[step]);
      const isStepComplete = next[step] === questionsPerStep[step];

      set({
        answersByStep: next,
        disabledButton: !isStepComplete, // locked until finished
      });
    },

    updateAnswerByStep: (step, value) => {
      const { answersByStep, questionsPerStep } = get();
      const next = [...answersByStep];

      next[step] = Math.min(value, questionsPerStep[step]);
      const isStepComplete = next[step] === questionsPerStep[step];

      set({
        answersByStep: next,
        disabledButton: !isStepComplete, // lock unless target is done
      });
    },

    /** Move forward one step **and** lock/unlock the button
     *  according to the *target* step’s completion state. */
    nextStep: () => {
      const { currentStep, answersByStep, questionsPerStep } = get();
      const targetStep = Math.min(currentStep + 1, questionsPerStep.length - 1);

      const isTargetComplete = answersByStep[targetStep] === questionsPerStep[targetStep];

      set({
        currentStep: targetStep,
        disabledButton: !isTargetComplete, // lock unless target is done
      });
    },

    updateQuestionsForStep: (step: number, questionCount: number) => {
      const { questionsPerStep, answersByStep, currentStep } = get();

      const updatedQuestions = [...questionsPerStep];
      updatedQuestions[step] = questionCount;

      const updatedAnswers = [...answersByStep];
      updatedAnswers[step] = Math.min(updatedAnswers[step], questionCount);

      const isStepComplete = updatedAnswers[currentStep] === updatedQuestions[currentStep];

      set({
        questionsPerStep: updatedQuestions,
        answersByStep: updatedAnswers,
        disabledButton: !isStepComplete,
      });
    },
    setSelectedFeature: (featureKey, vlaue) => {
      const { selectedFeatureStep } = get();

      if (!selectedFeatureStep[featureKey]) {
        set({
          selectedFeatureStep: {
            ...selectedFeatureStep,
            [featureKey]: vlaue,
          },
        });
      }
    },
    getSelectedFeature: (featureKey) => {
      const { selectedFeatureStep } = get();
      return selectedFeatureStep[featureKey];
    },

    setInitialInputStep: (featureKey, vlaue) => {
      const { initialInputsStep } = get();

      if (!initialInputsStep[featureKey]) {
        set({
          initialInputsStep: {
            ...initialInputsStep,
            [featureKey]: vlaue,
          },
        });
      }
    },
    getInitialInputStep: (featureKey) => {
      const { initialInputsStep } = get();
      return initialInputsStep[featureKey];
    },

    setSelectedFeatureMessagesData: (featureName, message) => {
      const { selectedFeatureMessagesData } = get();
      set({
        selectedFeatureMessagesData: {
          ...selectedFeatureMessagesData,
          [featureName]: [...(selectedFeatureMessagesData[featureName] || []), message],
        },
      });
    },
    addFeature: (featureId) =>
      set((state) => {
        const updatedFeaturesToAdd = [...state.featuresData.featuresToAdd, featureId];
        return {
          featuresData: {
            ...state.featuresData,
            featuresToAdd: updatedFeaturesToAdd,
          },
        };
      }),

    // Delete a feature
    deleteFeature: (featureId, instanceId) =>
      set((state) => {
        const updatedFeaturesToDelete = [...state.featuresData.featuresToDelete, { featureId, instanceId }];
        return {
          featuresData: {
            ...state.featuresData,
            featuresToDelete: updatedFeaturesToDelete, // Add feature to featuresToDelete
          },
        };
      }),

    removeFeature: (featureId: string) =>
      set((state) => {
        // Remove featureId from featuresToAdd
        const updatedFeaturesToAdd = state.featuresData.featuresToAdd.filter((feature) => feature !== featureId);

        // Remove feature from featuresToUpdate that matches featureId
        const updatedFeaturesToUpdate = state.featuresData.featuresToUpdate.filter(
          (feature) => feature.featureId !== featureId
        );

        // Remove feature from featuresToDelete (if it exists there)
        const updatedFeaturesToDelete = state.featuresData.featuresToDelete.filter(
          (feature) => feature.featureId !== featureId
        );

        return {
          featuresData: {
            ...state.featuresData,
            featuresToAdd: updatedFeaturesToAdd,
            featuresToUpdate: updatedFeaturesToUpdate,
            featuresToDelete: updatedFeaturesToDelete,
          },
        };
      }),

    removeFeatureByInstanceId: (featureId: string, instanceId: string) => {
      set((state) => {
        // Remove from featuresToDelete based on both featureId and instanceId
        const updatedFeaturesToDelete = state.featuresData.featuresToDelete.filter(
          (feature) => feature.instanceId !== instanceId || feature.featureId !== featureId
        );

        // Remove from featuresToUpdate based on both featureId and instanceId
        const updatedFeaturesToUpdate = state.featuresData.featuresToUpdate.filter(
          (feature) => feature.instanceId !== instanceId || feature.featureId !== featureId
        );

        return {
          featuresData: {
            ...state.featuresData,
            featuresToDelete: updatedFeaturesToDelete,
            featuresToUpdate: updatedFeaturesToUpdate,
          },
        };
      });
    },

    // Update a feature
    updateFeature: (featureId, instanceId) =>
      set((state) => {
        const updatedFeaturesToUpdate = [...state.featuresData.featuresToUpdate, { featureId, instanceId }];
        return {
          featuresData: {
            ...state.featuresData,
            featuresToUpdate: updatedFeaturesToUpdate,
          },
        };
      }),

    isInstanceIdInDelete: (featureId: string, instanceId: string) => {
      const state = get();
      return (
        state.featuresData.featuresToDelete?.some(
          (feature) => feature.instanceId === instanceId && feature.featureId === featureId
        ) || false
      );
    },

    isInstanceIdInUpdate: (featureId: string, instanceId: string) => {
      const state = get();
      return (
        state.featuresData.featuresToUpdate?.some(
          (feature) => feature.instanceId === instanceId && feature.featureId === featureId
        ) || false
      );
    },

    addFeatureToAdd: (id: string) =>
      set((state) => {
        const updatedFeaturesToAdd = [...state.featuresData.featuresToAdd, id];
        return {
          featuresData: {
            ...state.featuresData,
            featuresToAdd: updatedFeaturesToAdd,
          },
        };
      }),

    removeFeatureFromAdd: (id: string) =>
      set((state) => {
        const updatedFeaturesToAdd = state.featuresData.featuresToAdd.filter((feature) => feature !== id);
        return {
          featuresData: {
            ...state.featuresData,
            featuresToAdd: updatedFeaturesToAdd,
          },
        };
      }),

    setData: (button1, button2) => {
      set((state) => ({
        data: {
          ...state.data,
          [button1.name]: button1.value,
          [button2.name]: button2.value,
        },
      }));
    },
  };
});
